import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import {
  Accordion,
  AccordionItem,
  Button,
  InlineLoading,
  InlineNotification,
  Link,
  Loading,
  Tile,
} from '@carbon/react';
import { SidePanel } from '@carbon/ibm-products';
import {
  CheckmarkOutline,
  Close,
  Edit,
  Information,
  OrderDetails,
  Restart,
  SettingsAdjust,
  TrashCan,
} from '@carbon/react/icons';
import { formatResource } from 'utils/misc';
import { formatConfidence } from 'utils/number';
import request from 'lib/request';
import type { SectionMutationAtom } from 'lib/atoms/section-mutation';
import type { ResourceAtom } from 'lib/atoms';
import type {
  ConfirmedMatch,
  StructuringResource,
  WellLifecycleResponse,
  WellListItemResources,
  WellSectionStatus,
  WellSuggestion,
} from 'pages/afe-catalog/structuring/types';
// Pure Well components — no doctype coupling; both AFE and DO already consume
// these afe-catalog copies, so the shared shell points at the same modules.
import { WellWorkflowStatusBadge } from '../../afe-catalog/structuring/well-workflow-status-badge';
import { WellSuggestionCard } from '../../afe-catalog/structuring/well-suggestion-card';
import { WellNoMatchCard } from '../../afe-catalog/structuring/well-no-match-card';
import { WellConfidenceBreakdownTable } from '../../afe-catalog/structuring/well-confidence-breakdown-table';
import { WellScoringGlossaryPanel } from '../../afe-catalog/structuring/well-scoring-glossary-panel';
import styles from '../../afe-catalog/structuring/structuring-module.module.scss';
import wellStyles from '../../afe-catalog/structuring/wells.module.scss';

// ---------------------------------------------------------------------------
// Shared Well Match Review drill-in. ONE implementation for both the AFE and DO
// Well sections (they were ~90% identical clones). The staged tile + suggestion
// strip + per-well Agent Details SidePanel + activity log, with the full
// confirm / revert / rerun / remove / restore lifecycle, live here; every
// doctype divergence is injected via the `config` prop:
//   • the wells atom bundle (detail resource + lifecycle mutations) + URL builders;
//   • the staged-tile field list (AFE renders formation/basin/depths/locations…,
//     DO renders operator/property…) via `stagedTileFields`;
//   • the removal URL (AFE prefers a HATEOAS `removal` href, DO builds it by id);
//   • the edit href (AFE reads `resources.update`, DO builds it by id);
//   • the slide-in shell selector (`.afe-structuring-shell` vs `.do-structuring-shell`);
//   • the doctype WellEdit / ChangeMatch panels;
//   • the activity-log element (AFE scopes by afeTargetId, DO by
//     divisionOrderTargetId — different feed endpoints).
// The pure suggestion / confidence / glossary components + SCSS are shared
// verbatim from afe-catalog. Keep this behavior-identical to both callers.
// ---------------------------------------------------------------------------

/** Minimal staged-well contract the shared shell reads directly. */
export interface BaseStagedWell {
  wellName: string | null;
}

/** Well overview row, generic over the doctype's staged-well shape. */
export interface SharedWellListItem<S extends BaseStagedWell> {
  id: number;
  status: WellSectionStatus;
  userEdited: boolean;
  userAdded: boolean;
  stagedWell: S;
  confirmedMatch: ConfirmedMatch | null;
  pendingSuggestionsCount: number;
  noMatch: boolean;
  agentRunInProgress?: boolean;
  resources: WellListItemResources;
}

/**
 * Match Review detail resources. Superset of both doctypes: AFE emits
 * update / removal / restoration; DO emits only restoration (the rest are built
 * by id). All optional so either doctype's detail is assignable.
 */
export interface SharedMatchReviewResources extends WellListItemResources {
  update?: StructuringResource;
  removal?: StructuringResource;
  restoration?: StructuringResource;
}

/** GET .../wells/:id — generic over the doctype's staged-well shape. */
export interface SharedMatchReviewResponse<S extends BaseStagedWell> {
  id: number;
  status: WellSectionStatus;
  noMatch?: boolean;
  agentRunInProgress?: boolean;
  userEdited?: boolean;
  userAdded?: boolean;
  stagedWell: S;
  confirmedMatch?: ConfirmedMatch | null;
  suggestions: WellSuggestion[];
  resources: SharedMatchReviewResources;
}

// Both doctypes build these from the SAME shared atom factories, so the atom
// types are identical — named via the factories' exported aliases.
type WellMutationAtom = SectionMutationAtom<WellLifecycleResponse>;
type WellDetailAtom<S extends BaseStagedWell> = ResourceAtom<
  SharedMatchReviewResponse<S>
>;

export interface SharedWellEditPanelProps<S extends BaseStagedWell> {
  open: boolean;
  documentId: number | string;
  well: SharedWellListItem<S>;
  staged: S;
  editHref?: string;
  onClose: () => void;
  onSaved: () => void;
}

export interface SharedChangeMatchPanelProps {
  open: boolean;
  matchCandidateHref?: string;
  stagedWellName: string;
  onClose: () => void;
  onChanged: () => void;
}

/** Doctype wiring for the shared Well Match Review. */
export interface WellReviewConfig<S extends BaseStagedWell> {
  /** Match Review detail resource atom factory (auto-fetches on mount). */
  createWellDetailResourceAtom: (href: string | undefined) => WellDetailAtom<S>;
  wellDetailUrl: (
    documentId: number | string,
    wellId: number | string
  ) => string;
  confirmMatchAtom: WellMutationAtom;
  revertMatchAtom: WellMutationAtom;
  rerunSuggestionsAtom: WellMutationAtom;
  removeWellAtom: WellMutationAtom;
  restoreWellAtom: WellMutationAtom;
  /**
   * Full ordered staged-tile field list. Each descriptor is one `<dt>/<dd>` row;
   * "always render" fields pass a `'—'` fallback value and conditional fields are
   * omitted from the array — preserving the per-doctype row set + alignment.
   */
  stagedTileFields: (staged: S) => { label: string; value: string }[];
  /** Remove-well URL (AFE prefers the HATEOAS removal href; DO builds it by id). */
  removalUrl: (
    detail: SharedMatchReviewResponse<S> | undefined,
    detailHref: string
  ) => string;
  /** Edit href for the WellEdit panel (AFE reads resources.update; DO omits). */
  wellEditHref?: (
    detail: SharedMatchReviewResponse<S> | undefined
  ) => string | undefined;
  /** Slide-in shell root the SidePanels anchor to. */
  shellSelector: string;
  WellEditPanel: FC<SharedWellEditPanelProps<S>>;
  ChangeMatchPanel: FC<SharedChangeMatchPanelProps>;
  /** Doctype activity-log element, scoped to this well's target. */
  renderAuditLog: (documentId: number | string, wellId: number) => ReactNode;
}

export interface WellReviewMatchProps<S extends BaseStagedWell> {
  documentId: number | string;
  /** The overview row for this well (gives us the id + HATEOAS hrefs). */
  well: SharedWellListItem<S>;
  index: number;
  /**
   * Total well count. The shell's section header owns the "N of M" navigator
   * (chevrons + label), so the per-well title here only shows "Well {N} •
   * {name}". Kept on the props for a stable export signature.
   */
  total: number;
  /** Refetch the overview list after any mutation. */
  onMutated: () => void;
  /** Doctype wiring (stable module-level object per doctype). */
  config: WellReviewConfig<S>;
}

const pickRecommendedId = (suggestions: WellSuggestion[]): number | undefined =>
  suggestions.find((s) => s.isRecommended)?.id ?? suggestions[0]?.id;

export function WellReviewMatch<S extends BaseStagedWell>({
  documentId,
  well,
  index,
  onMutated,
  config,
}: WellReviewMatchProps<S>) {
  const detailHref = config.wellDetailUrl(documentId, well.id);
  const detailAtom = useMemo(
    () => config.createWellDetailResourceAtom(detailHref),
    [config, detailHref]
  );
  const [detailResource, reloadDetail] = useAtom(detailAtom);
  const { loading, error, data: detail } = formatResource(detailResource);

  const confirmMatch = useSetAtom(config.confirmMatchAtom);
  const revertMatch = useSetAtom(config.revertMatchAtom);
  const rerunSuggestions = useSetAtom(config.rerunSuggestionsAtom);
  const removeWell = useSetAtom(config.removeWellAtom);
  const restoreWell = useSetAtom(config.restoreWellAtom);

  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [agentDetailsOpen, setAgentDetailsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [changeMatchOpen, setChangeMatchOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [rerunPending, setRerunPending] = useState(false);

  const suggestions = useMemo(
    () => detail?.suggestions ?? [],
    [detail?.suggestions]
  );
  const status = detail?.status ?? well.status;
  const isReviewed = status === 'complete';
  const isRemoved = status === 'removed';

  // The confirmed suggestion (on a reviewed well) is whichever candidate matches
  // the server-emitted confirmedMatch — NOT the recommended/first one, which is
  // wrong whenever the user confirmed a non-recommended candidate. Match on kind
  // + suggestedId so a Well#5 and an ImportedWell#5 never cross-match. undefined
  // when nothing is confirmed yet, or when the confirmed well isn't among the
  // shown suggestions (e.g. confirmed via Change Match to an off-list well) → no
  // tile is highlighted.
  const confirmedSuggestionId = useMemo<number | undefined>(() => {
    const cm = detail?.confirmedMatch;
    if (!cm) return undefined;
    const wantKind = cm.kind === 'existing_well' ? 'Well' : 'ImportedWell';
    const wantId = cm.kind === 'existing_well' ? cm.wellId : cm.importedWellId;
    if (wantId === null || wantId === undefined) return undefined;
    return suggestions.find(
      (s) => s.suggestedKind === wantKind && s.suggestedId === wantId
    )?.id;
  }, [detail?.confirmedMatch, suggestions]);

  // Default-select the confirmed suggestion on a reviewed well, else the
  // recommended (or first), until the user actively picks another.
  const effectiveSelectedId =
    selectedId ?? confirmedSuggestionId ?? pickRecommendedId(suggestions);

  // Reset transient state when navigating to a different well.
  useEffect(() => {
    setSelectedId(undefined);
    setActionError(null);
    setRerunPending(false);
  }, [well.id]);

  // Mirror the server's in-flight flag into local pending state so a refresh
  // (or returning to a well mid-run) shows the running indicator.
  const detailAgentRunning = detail?.agentRunInProgress ?? false;
  useEffect(() => {
    if (detailAgentRunning) setRerunPending(true);
  }, [detailAgentRunning]);

  // While a rerun is pending, poll the detail endpoint until the agent run
  // finishes, then reload the detail + overview in one final refresh.
  useEffect(() => {
    if (!rerunPending) return undefined;
    let cancelled = false;
    const id = window.setInterval(async () => {
      const res = await request.get<SharedMatchReviewResponse<S>>(detailHref);
      if (cancelled || 'error' in res) return;
      if (!res.agentRunInProgress) {
        cancelled = true;
        window.clearInterval(id);
        setRerunPending(false);
        reloadDetail();
        onMutated();
      }
    }, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [rerunPending, detailHref, reloadDetail, onMutated]);

  const refreshAll = () => {
    reloadDetail();
    onMutated();
  };

  const hrefFor = (
    key: 'confirmation' | 'matchCandidate' | 'suggestionRerun'
  ) => detail?.resources?.[key]?.href ?? well.resources?.[key]?.href;

  const handleMarkCorrect = () => {
    const href = hrefFor('confirmation');
    if (!href || !effectiveSelectedId) return;
    setActionError(null);
    confirmMatch({
      url: href,
      method: 'POST',
      data: { suggestion_id: effectiveSelectedId },
      onSuccess: refreshAll,
      onError: (m) => setActionError(m ?? 'Failed to confirm match.'),
    });
  };

  const handleRemoveCorrect = () => {
    const href = hrefFor('confirmation');
    if (!href) return;
    setActionError(null);
    revertMatch({
      url: href,
      method: 'DELETE',
      onSuccess: refreshAll,
      onError: (m) => setActionError(m ?? 'Failed to remove correct status.'),
    });
  };

  const handleRerun = () => {
    const href = hrefFor('suggestionRerun');
    if (!href) return;
    setActionError(null);
    rerunSuggestions({
      url: href,
      method: 'POST',
      onSuccess: () => {
        // Run is async — flip the banner on now; the poll reloads the detail +
        // overview when the agent finishes. Refresh the overview list so the
        // tree's per-well spinner flips on immediately.
        setRerunPending(true);
        onMutated();
      },
      onError: (m) => setActionError(m ?? 'Failed to rerun agent suggestions.'),
    });
  };

  const handleRemoveWell = () => {
    const url = config.removalUrl(detail, detailHref);
    setActionError(null);
    removeWell({
      url,
      method: 'DELETE',
      onSuccess: refreshAll,
      onError: (m) => setActionError(m ?? 'Failed to remove well.'),
    });
  };

  const handleUndoRemove = () => {
    const url =
      detail?.resources?.restoration?.href ?? `${detailHref}/restoration`;
    setActionError(null);
    restoreWell({
      url,
      method: 'POST',
      onSuccess: refreshAll,
      onError: (m) => setActionError(m ?? 'Failed to restore well.'),
    });
  };

  const staged = detail?.stagedWell ?? well.stagedWell;

  return (
    <div className={styles.matchReview}>
      <div className={styles.matchReviewHeaderBar}>
        <div className={styles.matchReviewTitleMeta}>
          <h4 className={styles.matchReviewName}>
            {`Well ${index + 1} • ${staged.wellName ?? 'Unnamed well'}`}
          </h4>
          <WellWorkflowStatusBadge
            status={status}
            userEdited={detail?.userEdited}
          />
        </div>
        <div className={styles.matchReviewActions}>
          {isRemoved ? (
            <Button kind="tertiary" size="sm" onClick={handleUndoRemove}>
              Undo Remove
            </Button>
          ) : isReviewed ? (
            <>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={Information}
                iconDescription="Scoring Glossary"
                tooltipPosition="bottom"
                tooltipAlignment="end"
                onClick={() => setGlossaryOpen((v) => !v)}
              />
              <Button
                kind="tertiary"
                size="sm"
                renderIcon={Close}
                onClick={handleRemoveCorrect}
              >
                Remove Correct Status
              </Button>
            </>
          ) : (
            <>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                renderIcon={Information}
                iconDescription="Scoring Glossary"
                tooltipPosition="bottom"
                tooltipAlignment="end"
                onClick={() => setGlossaryOpen((v) => !v)}
              />
              <Button
                kind="danger--tertiary"
                size="sm"
                hasIconOnly
                renderIcon={TrashCan}
                iconDescription="Delete Well"
                tooltipPosition="bottom"
                tooltipAlignment="end"
                onClick={handleRemoveWell}
              />
              <Button
                kind="tertiary"
                size="sm"
                hasIconOnly
                renderIcon={Restart}
                iconDescription={
                  rerunPending
                    ? 'Agent suggestions are generating…'
                    : 'Rerun Agent Suggestions'
                }
                tooltipPosition="bottom"
                tooltipAlignment="end"
                onClick={handleRerun}
                disabled={rerunPending}
              />
              <Button
                kind="tertiary"
                size="sm"
                hasIconOnly
                renderIcon={SettingsAdjust}
                iconDescription="Change Match"
                tooltipPosition="bottom"
                tooltipAlignment="end"
                onClick={() => setChangeMatchOpen(true)}
              />
              <Button
                kind="tertiary"
                size="sm"
                hasIconOnly
                renderIcon={OrderDetails}
                iconDescription={
                  suggestions.length > 0
                    ? 'Agent Details'
                    : 'Agent Details unavailable — no suggestions for this well yet.'
                }
                tooltipPosition="bottom"
                tooltipAlignment="end"
                disabled={suggestions.length === 0}
                onClick={() => setAgentDetailsOpen(true)}
              />
              <Button
                kind="primary"
                size="sm"
                renderIcon={CheckmarkOutline}
                disabled={!effectiveSelectedId || rerunPending}
                onClick={handleMarkCorrect}
              >
                Mark as Correct
              </Button>
            </>
          )}
        </div>
      </div>

      <p className={styles.sectionLabel}>Match Review</p>
      {rerunPending && (
        <div className={styles.rerunBanner} role="status" aria-live="polite">
          <div className={styles.rerunSpinner}>
            <InlineLoading description="" />
          </div>
          <span className={styles.rerunText}>
            <strong>Generating fresh suggestions…</strong> The well matching
            agent is running. New suggestions will appear here when it finishes.
          </span>
        </div>
      )}
      <p className={styles.sectionDesc}>
        {isRemoved ? (
          'This well has been removed. The matches below are no longer applicable.'
        ) : isReviewed ? (
          'This match has been confirmed. The confirmed well is highlighted below. You can review all matches in context.'
        ) : (
          <>
            Review the matches for the extracted well and select the best one.
            If none are correct, use <strong>Change Match</strong> to search for
            a different well in the database, or create an imported one. Use{' '}
            <strong>Delete</strong> if not relevant, and{' '}
            <strong>Mark as Correct</strong> once verified.
          </>
        )}
      </p>

      {actionError && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Error"
          subtitle={actionError}
          onCloseButtonClick={() => setActionError(null)}
        />
      )}
      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          title="Unable to load match review"
          subtitle={error}
        />
      )}

      {loading && !detail ? (
        <Loading
          withOverlay={false}
          small
          description="Loading match review…"
        />
      ) : (
        <div
          className={[
            wellStyles.tiles,
            isRemoved ? styles.matchesSectionDisabled : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div className={wellStyles.tileDocPane}>
            <Tile className={`${styles.tile} ${styles.tileDoc}`}>
              <div className={styles.tileHeader}>
                <p className={styles.tileEyebrow}>
                  Extracted Well from Document
                </p>
                <Button
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  renderIcon={Edit}
                  iconDescription={
                    isReviewed
                      ? 'To edit, un-match well first'
                      : 'Edit Well Details'
                  }
                  tooltipPosition="bottom"
                  tooltipAlignment="end"
                  disabled={isReviewed || isRemoved}
                  onClick={() => setEditOpen(true)}
                />
              </div>
              <p className={styles.tileName}>
                {staged.wellName ?? 'Unnamed well'}
              </p>
              {/* Field order + fallbacks are owned by the doctype config's
                  `stagedTileFields`. "Always render" rows carry a '—' fallback so
                  the row count stays stable with the suggestion tile; conditional
                  rows are omitted from the list entirely. */}
              <dl className={styles.tileFields}>
                {config.stagedTileFields(staged).map((f) => (
                  <div key={f.label} className={styles.tileField}>
                    <dt>{f.label}</dt>
                    <dd>{f.value}</dd>
                  </div>
                ))}
              </dl>
            </Tile>
          </div>

          <div className={wellStyles.tileMatches}>
            {rerunPending ? (
              // Old suggestions are being superseded and regenerated — hide the
              // stale cards and show a loader where the fresh ones will appear.
              <Loading
                withOverlay={false}
                small
                description="Generating fresh suggestions…"
              />
            ) : suggestions.length === 0 ? (
              <WellNoMatchCard
                onChangeMatch={
                  isReviewed || isRemoved
                    ? undefined
                    : () => setChangeMatchOpen(true)
                }
              />
            ) : (
              suggestions.map((suggestion, i) => (
                <WellSuggestionCard
                  key={suggestion.id}
                  suggestion={suggestion}
                  index={i}
                  wellId={well.id}
                  checked={effectiveSelectedId === suggestion.id}
                  disabled={isReviewed || isRemoved}
                  isConfirmedTile={
                    isReviewed && confirmedSuggestionId === suggestion.id
                  }
                  onSelect={setSelectedId}
                />
              ))
            )}
          </div>
        </div>
      )}

      <div className={styles.auditLogWrap}>
        {/* The heading ("Activity Log — N events") is rendered by the shared
            AuditLogTable. The doctype config scopes the feed to this well's
            target (AFE afeTargetId / DO divisionOrderTargetId). */}
        {config.renderAuditLog(documentId, well.id)}
      </div>

      {/* Per-well Agent Details — run summary + a per-suggestion confidence
          breakdown. Driven entirely off the suggestions/confidenceFactors
          already in the Match Review response (no extra fetch). */}
      <SidePanel
        open={agentDetailsOpen}
        onRequestClose={() => setAgentDetailsOpen(false)}
        title="Agent Details"
        subtitle={
          <span>
            Match evaluation for{' '}
            <Link
              href="#"
              onClick={(e: React.MouseEvent) => e.preventDefault()}
            >
              {staged.wellName ?? 'this well'}
            </Link>
          </span>
        }
        slideIn
        selectorPageContent={config.shellSelector}
        placement="right"
        size="lg"
      >
        <div className={wellStyles.agentDetailsBody}>
          <div>
            <p className={wellStyles.agentDetailsHeading}>Summary</p>
            <p className={wellStyles.agentDetailsSummary}>
              The Well Matching Agent evaluated{' '}
              <strong>{staged.wellName ?? 'this well'}</strong> against the well
              master and returned <strong>{suggestions.length}</strong>{' '}
              candidate{suggestions.length === 1 ? '' : 's'}. The top match was
              selected based on a weighted comparison of API/UWI, well name,
              surface and bottom hole locations, and legal description.
            </p>
          </div>

          <Accordion>
            {suggestions.map((s, i) => (
              <AccordionItem
                key={s.id}
                title={`AI Suggestion ${i + 1}${
                  s.isRecommended ? ' (Recommended)' : ''
                } — Confidence Breakdown — ${formatConfidence(
                  s.confidenceScore
                )}`}
              >
                <WellConfidenceBreakdownTable factors={s.confidenceFactors} />
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </SidePanel>

      <WellScoringGlossaryPanel
        open={glossaryOpen}
        onRequestClose={() => setGlossaryOpen(false)}
        selectorPageContent={config.shellSelector}
      />
      <config.WellEditPanel
        open={editOpen}
        documentId={documentId}
        well={well}
        staged={staged}
        editHref={config.wellEditHref?.(detail)}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          setEditOpen(false);
          refreshAll();
        }}
      />
      <config.ChangeMatchPanel
        open={changeMatchOpen}
        matchCandidateHref={hrefFor('matchCandidate')}
        stagedWellName={staged.wellName ?? ''}
        onClose={() => setChangeMatchOpen(false)}
        onChanged={() => {
          setChangeMatchOpen(false);
          refreshAll();
        }}
      />
    </div>
  );
}
